import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Grid,
  useTheme,
  Paper,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  FormControlLabel,
  Alert,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import InsightsIcon from '@mui/icons-material/Insights';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import { usePricing } from '../hooks/usePricing';

interface AlternativeSubscriptionAdvertisementProps {
  onProceed?: () => void;
  /**
   * Passed in rather than read from a store: this renders both from
   * /create-location, where no location exists in the URL yet, and from a
   * location's subscription tab. The two callers have different sources for it.
   */
  countryCode?: string;
  typeName?: string;
}

const AlternativeSubscriptionAdvertisement: React.FC<AlternativeSubscriptionAdvertisementProps> = ({ onProceed, countryCode, typeName }) => {
  const theme = useTheme();
  const [termsDialogOpen, setTermsDialogOpen] = useState(false);
  const [signupDialogOpen, setSignupDialogOpen] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showError, setShowError] = useState(false);
  
  // Display pricing comes from the same backend table the checkout charges from.
  const { pricing: currentPricing } = usePricing(countryCode);

  const handleOpenTermsDialog = () => {
    setTermsDialogOpen(true);
  };

  const handleCloseTermsDialog = () => {
    setTermsDialogOpen(false);
  };

  const handleStartSubscription = () => {
    setSignupDialogOpen(true);
    setTermsAccepted(false);
    setShowError(false);
  };

  const handleCloseSignupDialog = () => {
    setSignupDialogOpen(false);
    setShowError(false);
  };

  const handleTermsAcceptedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTermsAccepted(event.target.checked);
    if (event.target.checked) {
      setShowError(false);
    }
  };

  const handleProceed = () => {
    if (!termsAccepted) {
      setShowError(true);
      return;
    }
    
    // Close the dialog
    setSignupDialogOpen(false);
    
    // Call the provided onProceed function if it exists
    if (onProceed) {
      onProceed();
    }
  };

  return (
    <>
      <Card 
        elevation={4}
        sx={{ 
          borderRadius: 4,
          overflow: 'hidden',
          mt: 4,
          mb: 4,
          border: `1px solid ${theme.palette.grey[200]}`,
        }}
      >
        {/* Premium Banner */}
        <Box 
          sx={{ 
            bgcolor: theme.palette.primary.main,
            py: 1.5,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box 
            sx={{ 
              position: 'absolute', 
              top: -15, 
              left: -15, 
              width: 80, 
              height: 80, 
              bgcolor: theme.palette.primary.dark,
              transform: 'rotate(45deg)',
            }} 
          />
          
          <Typography 
            variant="h5" 
            align="center" 
            sx={{ 
              color: 'white', 
              fontWeight: 'bold',
              textShadow: '0px 1px 2px rgba(0,0,0,0.2)',
              zIndex: 2,
              position: 'relative',
            }}
          >
            ACTIVATE YOUR SUBSCRIPTION
          </Typography>
        </Box>

        <Grid container>
          {/* Left side - Features */}
          <Grid item xs={12} md={7}>
            <CardContent sx={{ p: 4 }}>
              <Typography 
                variant="h4" 
                component="h2" 
                color="primary"
                sx={{ 
                  fontWeight: 'bold',
                  mb: 3,
                }}
              >
                Transform Your Business with {typeName === 'COFFEE_SHOP' ? 'BrewPrks' : 'AnyPerks'}
              </Typography>
              
              <Typography 
                variant="subtitle1" 
                color="text.secondary"
                sx={{ mb: 4 }}
              >
                Join the many successful businesses using our innovative loyalty platform to boost customer engagement and increase revenue.
              </Typography>

              <Stack spacing={3}>
                <FeatureItem 
                  icon={<TrendingUpIcon fontSize="large" />}
                  title="Boost Customer Retention"
                  description={typeName === 'COFFEE_SHOP' ? "Use our proven streak mechanics to increase repeat visits by up to 40%" : "Boost customer retention with your own digital loyalty card."}
                />
                
                <FeatureItem 
                  icon={<SportsEsportsIcon fontSize="large" />}
                  title="Engaging Gamification"
                  description="Keep customers coming back with a fun and convenient digital reward experience"
                />
                
                <FeatureItem 
                  icon={<InsightsIcon fontSize="large" />}
                  title="Powerful Analytics"
                  description="Get to know your customers better and gain insights into their behavior"
                />
              </Stack>
            </CardContent>
          </Grid>

          {/* Right side - Pricing */}
          <Grid item xs={12} md={5}>
            <Paper 
              elevation={0}
              sx={{ 
                height: '100%', 
                bgcolor: theme.palette.grey[50],
                borderLeft: { xs: 'none', md: `1px solid ${theme.palette.grey[200]}` },
                borderTop: { xs: `1px solid ${theme.palette.grey[200]}`, md: 'none' },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                p: 4,
              }}
            >
              <Box 
                sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}>
                  <Typography variant="h3" component="span" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                    {currentPricing?.formatted ?? '...'}
                  </Typography>
                  <Typography variant="h6" component="span" sx={{ ml: 1, color: theme.palette.text.secondary }}>
                    per month
                  </Typography>
                </Box>
                
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
                  billed monthly
                </Typography>
              </Box>

              <Paper 
                elevation={0} 
                sx={{ 
                  bgcolor: theme.palette.primary.light + '20',
                  p: 3, 
                  borderRadius: 2,
                  mb: 3,
                  border: `1px dashed ${theme.palette.primary.main}`,
                }}
              >
                <Stack 
                  direction="row" 
                  spacing={1.5} 
                  alignItems="center" 
                  sx={{ mb: 1 }}
                >
                  <StarIcon color="primary" />
                  <Typography variant="h6" fontWeight="bold" color="primary.dark">
                    Premium Features:
                  </Typography>
                  <IconButton 
                    size="small" 
                    color="primary" 
                    onClick={handleOpenTermsDialog}
                    sx={{ 
                      ml: 'auto', 
                      bgcolor: theme.palette.primary.main + '15',
                      '&:hover': {
                        bgcolor: theme.palette.primary.main + '25',
                      }
                    }}
                    aria-label="View terms and conditions"
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Stack>
                
                <Divider sx={{ mb: 2 }} />
                
                <Stack spacing={1.5}>
                  <BenefitItem text="Unlimited stamps" />
                  <BenefitItem text="All premium features" />
                  <BenefitItem text="Full analytics access" />
                  <BenefitItem text="Priority support" />
                </Stack>
              </Paper>

              <Button 
                variant="contained" 
                size="large"
                fullWidth
                onClick={handleStartSubscription}
                sx={{ 
                  py: 1.5, 
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  borderRadius: 2,
                  color: 'white',
                }}
              >
                Activate Subscription
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Card>

      {/* Terms and Conditions Info Dialog */}
      <Dialog 
        open={termsDialogOpen} 
        onClose={handleCloseTermsDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2
        }}>
          <Typography variant="h6" component="div" fontWeight="bold">
            Subscription Terms & Conditions
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleCloseTermsDialog}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <List>
            <ListItem alignItems="flex-start" sx={{ mb: 2 }}>
              <ListItemIcon>
                <CreditCardIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Immediate Billing"
                secondary="Your subscription will be activated immediately upon payment confirmation. You will be charged the full monthly fee right away."
              />
            </ListItem>
            <ListItem alignItems="flex-start" sx={{ mb: 2 }}>
              <ListItemIcon>
                <NotificationsActiveIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Monthly Recurring Billing"
                secondary="Your subscription will automatically renew each month on the same date. You will receive email notifications before each billing cycle."
              />
            </ListItem>
            <ListItem alignItems="flex-start">
              <ListItemIcon>
                <MoneyOffIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Cancellation Policy"
                secondary="You can cancel your subscription at any time. Cancellation will take effect at the end of your current billing period. No refunds are provided for partial months."
              />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${theme.palette.divider}`, px: 3, py: 2 }}>
          <Button onClick={handleCloseTermsDialog} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Signup Dialog with Terms Acceptance */}
      <Dialog 
        open={signupDialogOpen} 
        onClose={handleCloseSignupDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2
        }}>
          <Typography variant="h6" component="div" fontWeight="bold">
            Activate Your Subscription
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleCloseSignupDialog}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Before proceeding with your subscription activation, please review our terms and conditions:
          </Typography>
          
          <Paper 
            elevation={0}
            sx={{ 
              bgcolor: theme.palette.grey[50], 
              p: 2, 
              my: 2, 
              borderRadius: 2,
              border: `1px solid ${theme.palette.grey[200]}`,
              maxHeight: '250px',
              overflow: 'auto'
            }}
          >
            <List dense>
              <ListItem alignItems="flex-start" sx={{ mb: 1 }}>
                <ListItemIcon>
                  <CreditCardIcon color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Immediate Billing"
                  secondary="Your subscription will be activated immediately upon payment confirmation. You will be charged the full monthly fee right away."
                />
              </ListItem>
              <ListItem alignItems="flex-start" sx={{ mb: 1 }}>
                <ListItemIcon>
                  <NotificationsActiveIcon color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Monthly Recurring Billing"
                  secondary="Your subscription will automatically renew each month on the same date. You will receive email notifications before each billing cycle."
                />
              </ListItem>
              <ListItem alignItems="flex-start">
                <ListItemIcon>
                  <MoneyOffIcon color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Cancellation Policy"
                  secondary="You can cancel your subscription at any time. Cancellation will take effect at the end of your current billing period. No refunds are provided for partial months."
                />
              </ListItem>
            </List>
          </Paper>
          
          {showError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              You must accept the terms and conditions to proceed.
            </Alert>
          )}
          
          <FormControlLabel
            control={
              <Checkbox 
                checked={termsAccepted} 
                onChange={handleTermsAcceptedChange}
                color="primary"
              />
            }
            label="I accept the terms and conditions"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${theme.palette.divider}`, px: 3, py: 2 }}>
          <Button onClick={handleCloseSignupDialog} variant="outlined">
            Cancel
          </Button>
          <Button 
            onClick={handleProceed} 
            variant="contained" 
            color="primary"
            disabled={!termsAccepted}
          >
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Helper components
const FeatureItem: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
}> = ({ icon, title, description }) => {
  const theme = useTheme();
  
  return (
    <Stack direction="row" spacing={2} alignItems="flex-start">
      <Box 
        sx={{ 
          color: theme.palette.primary.main,
          mt: 0.5,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {description}
        </Typography>
      </Box>
    </Stack>
  );
};

const BenefitItem: React.FC<{ text: string }> = ({ text }) => {
  const theme = useTheme();
  
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <CheckCircleIcon fontSize="small" color="primary" />
      <Typography variant="body1">
        {text}
      </Typography>
    </Stack>
  );
};

export default AlternativeSubscriptionAdvertisement;